import { Reveal, Eyebrow, H2, Section } from "./ui";
import { MessageCircle, KeyRound, CreditCard, Printer, Check } from "lucide-react";

const WEEKS = [
  ["Semana 1", "Cansancio y reacciones", "Identificar señales de saturación, practicar una pausa y revisar la carga mental.", "var(--butter)"],
  ["Semana 2", "Rabietas y acuerdos", "Preparar un plan común para los momentos difíciles y hablar del reparto de tareas.", "#F2C3AE"],
  ["Semana 3", "Pareja y continuidad", "Crear pequeños gestos y acuerdos de equipo que podáis mantener en casa.", "#C9D8C5"],
];

const PLANNERS = ["Reparto de tareas y carga mental", "Rutina de sueño y noches", "Mañanas sin gritos", "Plan semanal de pareja", "Conversación de equipo en 15 minutos"];

export const Soporte = () => (
  <Section id="soporte" testid="section-soporte" className="bg-[var(--cream-2)]">
    <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
      <Reveal>
        <Eyebrow>Esta vez no lo haces en solitario</Eyebrow>
        <H2>Tus dudas, resueltas por WhatsApp mientras haces el reto.</H2>
        <p className="mt-6 text-lg text-[#5a4f46]">Leer consejos es fácil. Lo difícil es aplicarlos en <b className="text-[var(--ink)]">tu</b> casa, con <b className="text-[var(--ink)]">tu</b> hijo/a, con <b className="text-[var(--ink)]">tu</b> pareja y con <b className="text-[var(--ink)]">tu</b> cansancio. Por eso, durante los 21 días, nuestro equipo responde todas tus dudas.</p>
        <ul className="mt-6 space-y-3">
          {["Dudas libres durante todo el reto, por escrito o por audio.", "Te respondemos personalmente en un máximo de 48 h laborables.", "Adaptáis los ejercicios a vuestra situación real, no a la de un libro."].map((t) => (
            <li key={t} className="flex gap-3"><Check className="shrink-0 text-[var(--sage-deep)]" />{t}</li>
          ))}
        </ul>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="bg-white rounded-[2rem] p-5 sm:p-6 shadow-[0_20px_40px_-20px_rgba(42,37,33,0.4)] space-y-3 max-w-sm mx-auto" data-testid="support-chat-preview">
          <p className="text-xs text-center text-[#7a6c5e]">Ejemplo de conversación</p>
          <div className="bg-[#DCF3D4] rounded-2xl rounded-tr-sm p-3 ml-8 text-sm">Día 11: hemos hecho el plan para las rabietas, pero al salir del parque mi pareja cede siempre. ¿Qué hacemos?</div>
          <div className="bg-[var(--cream)] rounded-2xl rounded-tl-sm p-3 mr-8 text-sm">Pasa muchísimo. Probad esto: antes de salir, decidid quién actúa ese día y la otra persona solo acompaña. Contadnos cómo os va mañana. — Equipo En Dos Mundos</div>
        </div>
      </Reveal>
    </div>
  </Section>
);

export const Incluye = () => (
  <Section id="incluye" testid="section-incluye">
    <Reveal><Eyebrow>El recorrido</Eyebrow><H2 className="max-w-3xl">21 días de prácticas breves para vuestra vida diaria.</H2></Reveal>
    <div className="mt-10">
      {WEEKS.map(([w, t, d, color], i) => (
        <Reveal key={w} delay={i * 0.06}>
          <div className="rounded-3xl p-7 sm:p-9 grid sm:grid-cols-[180px_1fr] gap-4" style={{ background: color }} data-testid={`week-card-${i + 1}`}>
            <p className="font-hand text-3xl">{w}</p>
            <div><p className="font-display text-2xl sm:text-3xl">{t}</p><p className="mt-2 text-[#4a4038]">{d}</p></div>
          </div>
        </Reveal>
      ))}
    </div>
    <Reveal><p className="mt-8 text-[#5a4f46]">Para conseguirlo: <b className="text-[var(--ink)]">10 minutos al día</b> con un ejercicio práctico, <b className="text-[var(--ink)]">dudas por WhatsApp</b> con nuestro equipo y <b className="text-[var(--ink)]">5 planificadores</b> para llevarlo a la vida real.</p></Reveal>
    <Reveal>
      <figure className="mt-8 overflow-hidden rounded-2xl bg-white">
        <img src="/equipo-en-calma-conversacion.png" alt="Ilustración de una conversación de pareja para organizar una tarea compartida" loading="lazy" decoding="async" className="w-full aspect-[16/7] object-cover" />
        <figcaption className="px-4 py-3 text-sm text-[#5a4f46]">Nombrar una necesidad, escuchar y acordar un siguiente paso.</figcaption>
      </figure>
    </Reveal>
    <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-4">
      {PLANNERS.map((p, i) => (
        <Reveal key={p} delay={i * 0.06}>
          <div className="bg-white rounded-2xl p-5 aspect-[3/4] flex flex-col shadow-[0_10px_30px_-15px_rgba(42,37,33,0.35)] hover:rotate-[-1.5deg] transition-transform" data-testid={`planner-preview-${i}`}>
            <Printer size={18} className="text-[var(--terra)]" />
            <p className="font-display text-lg mt-3 leading-tight">{p}</p>
            <div className="mt-auto space-y-2">{[0, 1, 2, 3].map((line) => <div key={line} className="h-px bg-[#d9cbb8]" />)}</div>
          </div>
        </Reveal>
      ))}
    </div>
  </Section>
);

const STEPS = [
  [MessageCircle, "Completa el formulario", "Déjanos tu nombre y WhatsApp; el correo electrónico es opcional."],
  [CreditCard, "Paga con tarjeta en Stripe", "Después del formulario irás al checkout seguro de Stripe para completar el pago único de 39 €."],
  [KeyRound, "Recibe tu acceso", "Cuando verifiquemos el pago, te enviaremos el código por WhatsApp para entrar desde tu móvil u ordenador."],
];

export const ComoFunciona = () => (
  <Section id="como-funciona" testid="section-como-funciona" className="bg-[var(--ink)] text-[var(--cream)]">
    <Reveal><Eyebrow color="var(--butter)">Cómo funciona</Eyebrow><H2>Compra online y empieza con acompañamiento.</H2></Reveal>
    <div className="mt-12 grid md:grid-cols-3 gap-8">
      {STEPS.map(([Icon, t, d], i) => (
        <Reveal key={t} delay={i * 0.08}>
          <div data-testid={`step-${i + 1}`}>
            <div className="flex items-center gap-3"><span className="font-display text-5xl text-[var(--accent)]">{i + 1}</span><Icon /></div>
            <p className="text-xl font-semibold mt-4">{t}</p>
            <p className="text-white/70 mt-1">{d}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </Section>
);
